import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { disclosureConfigQueryOptions } from '@/api/loans-api'
import type { DisclosureHeadlineRate } from '@/api/loans-types'
import type { DualControlResult } from '@/api/types'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

import { DISCLOSURE_HEADLINE_LABELS } from './constants'
import { DualControlNotice, DualControlOutcome } from './dual-control'
import { LOAN_PERMISSIONS, useLoanPermissions } from './permissions'
import { loanPaths, useLoanNavigate } from './loan-paths'
import { FormField, NoteField, OperationForm } from './operation-form'
import { useSetDisclosureConfig } from './mutations'

/**
 * Choosing which rate headlines the pre-agreement disclosure — questionnaire item 9's
 * "make this configurable", made so.
 *
 * The choice is prominence, never disclosure: the document prints the quoted rate, the nominal
 * annual rate and the effective annual rate whichever one leads, and this screen says that in as
 * many words. It is a current-value row — overwritten under dual control, no effective date —
 * so what the panel above the form shows is the whole configuration state there is.
 */

const HEADLINE_RATES = Object.keys(DISCLOSURE_HEADLINE_LABELS) as DisclosureHeadlineRate[]

/** Unknown values from a newer server degrade to the raw code rather than crashing the panel. */
const headlineLabel = (rate: string): string =>
  (DISCLOSURE_HEADLINE_LABELS as Record<string, string>)[rate] ?? rate

function CurrentChoicePanel() {
  const current = useQuery(disclosureConfigQueryOptions())

  if (current.isPending) {
    return (
      <Alert tone="info">
        <AlertTitle>Reading the choice in force…</AlertTitle>
      </Alert>
    )
  }

  if (current.isError || !current.data) {
    return (
      <Alert tone="warning">
        <AlertTitle>The choice in force could not be read</AlertTitle>
        <AlertDescription>
          The form below still works — the server reads its own configuration when it renders a
          document, not this panel.
        </AlertDescription>
      </Alert>
    )
  }

  const config = current.data

  if (!config.isConfigured) {
    return (
      <Alert tone="warning">
        <AlertTitle>No choice has been recorded — the default leads</AlertTitle>
        <AlertDescription>
          Documents currently lead with the {headlineLabel(config.headlineRate)}, flagged
          provisional: the highest of the three figures, so the default cannot understate the cost
          of credit. The compliance assumptions register carries this as open under ref 9 until a
          choice is recorded here.
        </AlertDescription>
      </Alert>
    )
  }

  return (
    <Alert tone={config.isProvisional ? 'warning' : 'success'}>
      <AlertTitle>
        Documents lead with the {headlineLabel(config.headlineRate)}
        {config.isProvisional ? ' — still provisional' : ''}
      </AlertTitle>
      <AlertDescription>
        {config.sourceReference
          ? `Source: ${config.sourceReference}.`
          : 'No source reference was recorded.'}
        {config.isProvisional
          ? ' The assumptions register keeps reporting it under ref 9 until a confirmed choice is recorded.'
          : ''}
      </AlertDescription>
    </Alert>
  )
}

export function DisclosureConfigForm() {
  const navigate = useLoanNavigate()
  const permissions = useLoanPermissions()
  const set = useSetDisclosureConfig()

  const [headlineRate, setHeadlineRate] = useState<DisclosureHeadlineRate>('EffectiveCompounded')
  // Defaults to false because the ordinary reason to be on this screen is recording the
  // compliance officer's actual answer — the provisional state is what that ends.
  const [isProvisional, setIsProvisional] = useState(false)
  const [sourceReference, setSourceReference] = useState('')
  const [result, setResult] = useState<DualControlResult | null>(null)

  return (
    <div className="space-y-4">
      <CurrentChoicePanel />

      <OperationForm
        title="Choose the headline rate"
        description="All three rates stay on the document. This decides which one the member reads first."
        submitLabel="Submit the choice"
        isPending={set.isPending}
        error={set.isError ? set.error : null}
        onDone={() => navigate(loanPaths.products)}
        notice={
          <DualControlNotice
            action="Changing the disclosure headline"
            approvePermission={LOAN_PERMISSIONS.disclosureConfigApprove}
            canCountersign={permissions.canCountersignDisclosureConfig}
          />
        }
        outcome={
          result ? (
            <DualControlOutcome
              result={result}
              awaitingDescription="The choice is stored and documents still lead with whatever led before."
              appliedDescription="The choice is in force. The next disclosure rendered leads with it; documents already printed keep the lead they were signed under."
            />
          ) : null
        }
        onSubmit={() => {
          set.mutate(
            {
              headlineRate,
              isProvisional,
              sourceReference: sourceReference.trim() === '' ? null : sourceReference.trim(),
            },
            { onSuccess: setResult },
          )
        }}
        footnote="A current-value row: the previous choice is overwritten, and the audit trail is its only history. The document itself is unaffected in what it discloses — only in what it leads with."
      >
        <FormField
          id="disclosure-headline"
          label="Headline rate"
          required
          hint="The figure the document leads with, in a box above the loan terms."
        >
          <Select
            value={headlineRate}
            onValueChange={(next) => setHeadlineRate(next as DisclosureHeadlineRate)}
          >
            <SelectTrigger id="disclosure-headline">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {HEADLINE_RATES.map((rate) => (
                <SelectItem key={rate} value={rate}>
                  {DISCLOSURE_HEADLINE_LABELS[rate]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>

        <div className="flex items-start gap-2">
          <Checkbox
            id="disclosure-provisional"
            checked={isProvisional}
            onCheckedChange={(checked) => setIsProvisional(checked === true)}
            className="mt-0.5"
          />
          <div className="space-y-0.5">
            <Label htmlFor="disclosure-provisional" className="text-sm font-normal text-body">
              Provisional
            </Label>
            <p className="text-xs text-muted">
              Tick only when this is not yet the compliance officer&apos;s confirmed answer. A
              provisional choice stays on the assumptions register under ref 9.
            </p>
          </div>
        </div>

        <NoteField
          id="disclosure-source"
          label="Source reference"
          value={sourceReference}
          onChange={setSourceReference}
          placeholder="Questionnaire item 9 response, 29 Aug 2026"
          hint="The answer or directive the choice came from. Evidence, not decoration."
        />
      </OperationForm>
    </div>
  )
}
